import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { GabryxFooter } from '../components/GabryxBranding';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (pwd !== pwd2) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erreur lors de la réinitialisation.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-8 text-center"
          data-testid="reset-no-token">
          <h2 className="text-lg font-semibold text-[#111827] mb-2">Lien invalide</h2>
          <p className="text-sm text-[#4B5563] mb-4">Le lien de réinitialisation est manquant ou invalide.</p>
          <Link to="/mot-de-passe-oublie" className="text-[#0055FF] hover:underline text-sm">
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#111827]">CHANTIX</h1>
            <p className="text-sm text-[#6B7280] mt-1">Réinitialiser votre mot de passe</p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] p-6 sm:p-8">
            {success ? (
              <div className="text-center py-4" data-testid="reset-success">
                <CheckCircle2 size={48} className="text-emerald-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-[#111827] mb-2">Mot de passe réinitialisé</h2>
                <p className="text-sm text-[#4B5563]">Redirection vers la page de connexion...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="new-pwd">Nouveau mot de passe</Label>
                  <div className="relative mt-1">
                    <Input id="new-pwd" type={showPwd ? 'text' : 'password'}
                      required minLength={8} value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      placeholder="••••••••"
                      className="pr-10" data-testid="reset-pwd-input" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4B5563]">
                      {showPwd ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  <p className="text-xs text-[#6B7280] mt-1">Minimum 8 caractères.</p>
                </div>
                <div>
                  <Label htmlFor="pwd2">Confirmer le mot de passe</Label>
                  <Input id="pwd2" type={showPwd ? 'text' : 'password'}
                    required value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1" data-testid="reset-pwd2-input" />
                </div>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2"
                    data-testid="reset-error">{error}</div>
                )}
                <Button type="submit" disabled={loading}
                  className="w-full bg-[#0055FF] hover:bg-blue-700"
                  data-testid="reset-submit-btn">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Réinitialisation...</> : 'Réinitialiser le mot de passe'}
                </Button>
              </form>
            )}
            <div className="mt-6 text-center text-sm">
              <Link to="/login" className="text-[#0055FF] hover:underline" data-testid="reset-back-login">
                Retour à la connexion
              </Link>
            </div>
          </div>
        </div>
      </div>
      <GabryxFooter />
    </div>
  );
}

export default ResetPasswordPage;
